import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
} from '@chakra-ui/react';

export type AIProvider = 'gemini-free' | 'gemini' | 'claude' | 'gpt';

export interface AISettings {
  provider: AIProvider;
  apiKeys: {
    gemini?: string;
    claude?: string;
    gpt?: string;
  };
}

interface SettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: AISettings) => void;
}

const Settings: React.FC<SettingsProps> = ({ isOpen, onClose, onSave }) => {
  const [settings, setSettings] = useState<AISettings>({
    provider: 'gemini-free',
    apiKeys: {}
  });
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem('aiSettings');
      if (saved) {
        try {
          setSettings(JSON.parse(saved));
        } catch (error) {
          console.error('Failed to load AI settings:', error);
        }
      }
    }
  }, [isOpen]);

  const handleProviderChange = (provider: AIProvider) => {
    setSettings(prev => ({ ...prev, provider }));
  };

  const handleApiKeyChange = (provider: 'gemini' | 'claude' | 'gpt', key: string) => {
    setSettings(prev => ({
      ...prev,
      apiKeys: {
        ...prev.apiKeys,
        [provider]: key
      }
    }));
  };

  const handleSave = () => {
    // APIキーが必要なプロバイダーを選択している場合の検証
    if (settings.provider !== 'gemini-free') {
      const requiredKey = settings.provider;
      if (!settings.apiKeys[requiredKey]?.trim()) {
        toast({
          title: 'APIキーが必要です',
          description: `${getProviderDisplayName(settings.provider)}のAPIキーを入力してください`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    localStorage.setItem('aiSettings', JSON.stringify(settings));
    onSave(settings);
    toast({
      title: '設定を保存しました',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    onClose();
  };

  const getProviderDisplayName = (provider: AIProvider) => {
    switch (provider) {
      case 'gemini-free':
        return 'Gemini (無料版)';
      case 'gemini':
        return 'Gemini Pro';
      case 'claude':
        return 'Claude';
      case 'gpt':
        return 'GPT';
      default:
        return provider;
    }
  };

  const getProviderDescription = (provider: AIProvider) => {
    switch (provider) {
      case 'gemini-free':
        return '無料で利用可能（APIキー不要）';
      case 'gemini':
        return 'Google AI StudioのAPIキーが必要';
      case 'claude':
        return 'Anthropic Claude APIキーが必要';
      case 'gpt':
        return 'OpenAI GPT APIキーが必要';
      default:
        return '';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>AI設定</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <FormLabel>AIプロバイダー</FormLabel>
              <Select
                value={settings.provider}
                onChange={(e) => handleProviderChange(e.target.value as AIProvider)}
              >
                <option value="gemini-free">Gemini (無料版)</option>
                <option value="gemini">Gemini Pro</option>
                <option value="claude">Claude</option>
                <option value="gpt">GPT</option>
              </Select>
              <Text fontSize="sm" color="gray.600" mt={1}>
                {getProviderDescription(settings.provider)}
              </Text>
            </FormControl>

            {settings.provider !== 'gemini-free' && (
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  選択したAIサービスのAPIキーが必要です。APIキーは暗号化されてローカルに保存されます。
                </AlertDescription>
              </Alert>
            )}

            <VStack spacing={4} align="stretch">
              {settings.provider === 'gemini' && (
                <FormControl>
                  <FormLabel>Gemini Pro APIキー</FormLabel>
                  <Input
                    type="password"
                    value={settings.apiKeys.gemini || ''}
                    onChange={(e) => handleApiKeyChange('gemini', e.target.value)}
                    placeholder="AIzaSy..."
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Google AI Studioで取得できます
                  </Text>
                </FormControl>
              )}

              {settings.provider === 'claude' && (
                <FormControl>
                  <FormLabel>Claude APIキー</FormLabel>
                  <Input
                    type="password"
                    value={settings.apiKeys.claude || ''}
                    onChange={(e) => handleApiKeyChange('claude', e.target.value)}
                    placeholder="sk-ant-..."
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Anthropic Consoleで取得できます
                  </Text>
                </FormControl>
              )}

              {settings.provider === 'gpt' && (
                <FormControl>
                  <FormLabel>OpenAI APIキー</FormLabel>
                  <Input
                    type="password"
                    value={settings.apiKeys.gpt || ''}
                    onChange={(e) => handleApiKeyChange('gpt', e.target.value)}
                    placeholder="sk-..."
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    OpenAI Platform で取得できます
                  </Text>
                </FormControl>
              )}
            </VStack>

            <Box bg="gray.50" p={4} borderRadius="md">
              <Text fontSize="sm" fontWeight="bold" mb={2}>現在の設定</Text>
              <Text fontSize="sm">
                プロバイダー: {getProviderDisplayName(settings.provider)}
              </Text>
              {settings.provider !== 'gemini-free' && (
                <Text fontSize="sm" color={settings.apiKeys[settings.provider as keyof typeof settings.apiKeys] ? 'green.600' : 'red.600'}>
                  APIキー: {settings.apiKeys[settings.provider as keyof typeof settings.apiKeys] ? '設定済み' : '未設定'}
                </Text>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            キャンセル
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            保存
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default Settings;